import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Copy } from "lucide-react";
import { SEASONS, daysLeft } from "./seasons";
import { ProductCard, SecHead } from "./Products";
import { Garland, Ground } from "./FestiveArt";
import { eur } from "../../lib/api";

const codeOf = (s, promo) => promo?.code || null;
const pctOf = (promo) => (promo?.percent > 0 ? ` −${promo.percent} %` : "");

export function useCopy() {
  const [ok, setOk] = useState(false);
  const copy = (t) => { navigator.clipboard?.writeText(t); setOk(true); setTimeout(() => setOk(false), 1600); };
  return [ok, copy];
}

export function PromoBar({ seasonKey, promo }) {
  const s = SEASONS[seasonKey];
  const [ok, copy] = useCopy();
  if (!s) return null;
  const d = daysLeft(seasonKey);
  const code = codeOf(s, promo);
  if (!code) {
    return <div className={`relative z-50 px-4 py-2 text-center text-[13px] font-medium ${s.bar}`} data-testid="mq-promo-bar"><s.icon className="mr-1.5 inline h-4 w-4 -translate-y-px" />{s.label} chez Smarti · reconditionné en Belgique, garanti 36 mois<Link to={`/offres/${seasonKey}`} className="ml-2 font-semibold underline underline-offset-2" data-testid="mq-promo-bar-link">Voir les offres</Link></div>;
  }
  return (
    <div className={`relative z-50 px-4 py-2 text-center text-[13px] font-medium ${s.bar}`} data-testid="mq-promo-bar">
      <s.icon className="mr-1.5 inline h-4 w-4 -translate-y-px" />{s.label}<span className="hidden sm:inline"> chez Smarti</span> : code
      <button type="button" onClick={() => copy(codeOf(s, promo))} className="mx-1.5 inline-flex items-center gap-1 rounded-md border border-current/40 border-dashed px-2 py-0.5 font-bold tracking-wider" data-testid="mq-promo-bar-code">
        {codeOf(s, promo)}{ok ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>{pctOf(promo) && <b className="mr-1">{pctOf(promo)}</b>}
      {d && <span className="hidden opacity-80 sm:inline">· plus que {d} jour{d > 1 ? "s" : ""}</span>}
      <Link to={`/offres/${seasonKey}`} className="ml-2 font-semibold underline underline-offset-2" data-testid="mq-promo-bar-link">Voir les offres</Link>
    </div>
  );
}

export function SeasonPdp({ seasonKey, promo, price }) {
  const s = SEASONS[seasonKey];
  const [ok, copy] = useCopy();
  if (!s) return null;
  const code = codeOf(s, promo);
  const pct = promo?.percent > 0 ? promo.percent : 0;
  const d = daysLeft(seasonKey);
  return (
    <div className={`rise relative mt-6 overflow-hidden rounded-[24px] p-5 pr-28 sm:pr-40 ${s.bg}`} data-testid="pdp-season">
      {[0, 1, 2].map((k) => { const I = s.deco[k % s.deco.length]; return <I key={k} aria-hidden="true" className={`mq-deco pointer-events-none absolute opacity-30 ${s.accent} ${["right-[34%] top-3 h-5 w-5", "right-[46%] bottom-3 h-4 w-4", "left-[60%] top-[40%] h-3 w-3"][k]}`} style={{ animationDelay: `${k * 0.9}s` }} />; })}
      <div className="pointer-events-none absolute bottom-0 right-2 origin-bottom-right scale-[.62] sm:right-4 sm:scale-75 [&_[data-testid=mq-ground]]:!flex"><Ground seasonKey={seasonKey} pos="right-0" /></div>
      <span className={`relative inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${s.dark ? "bg-white/10" : "bg-white shadow-sm"} ${s.accent}`}><s.icon className="h-3.5 w-3.5" />{s.label}{d ? ` · J-${d}` : ""}</span>
      {code ? (
        <div className="relative mt-3">
          <p className="text-[15px] font-semibold leading-snug">{pct ? <>{`−${pct} % avec le code`} {price ? <span className={s.accent}>→ {eur(price * (1 - pct / 100))}</span> : null}</> : "Votre code du moment"}</p>
          <button type="button" onClick={() => copy(code)} className={`mt-2 inline-flex items-center gap-2 rounded-xl border-2 border-dashed px-3 py-1.5 text-[15px] font-bold tracking-[0.12em] transition-transform active:scale-95 ${s.dark ? "border-white/40" : "border-current"} ${s.dark ? "" : s.accent}`} data-testid="pdp-season-code">
            {code}{ok ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </button>
          <p className={`mt-1 text-[11px] ${s.dark ? "text-white/60" : "text-[var(--muted)]"}`}>{ok ? "Code copié !" : "Appliqué automatiquement au paiement"}</p>
        </div>
      ) : (
        <p className="relative mt-3 text-[15px] font-semibold leading-snug">{s.gift[0]} <span className={s.accent}>{s.gift[1]}</span></p>
      )}
    </div>
  );
}

export function SeasonDeco({ seasonKey }) {
  const s = SEASONS[seasonKey];
  if (!s) return null;
  const P = ["left-[6%] top-[8%] h-7 w-7", "right-[8%] top-[14%] h-5 w-5", "left-[10%] bottom-[16%] h-4 w-4", "right-[12%] bottom-[10%] h-6 w-6"];
  return P.map((c, k) => { const I = s.deco[k % s.deco.length]; return <I key={k} aria-hidden="true" className={`mq-deco pointer-events-none absolute z-[2] opacity-40 ${s.accent} ${c}`} style={{ animationDelay: `${k * 0.7}s` }} />; });
}

export function SeasonBand({ seasonKey, items, promo }) {
  const s = SEASONS[seasonKey];
  const [ok, copy] = useCopy();
  if (!s || !items.length) return null;
  return (
    <section className="py-10 sm:py-14" data-testid="mq-season-band">
      <div className="wrap">
        <div className={`relative overflow-hidden rounded-[32px] p-6 pt-24 sm:p-10 sm:pt-24 ${s.bg}`}>
          <Garland seasonKey={seasonKey} />
          <Ground seasonKey={seasonKey} pos="right-8" />
          {[0, 1, 2, 3].map((k) => { const I = s.deco[k % s.deco.length]; return <I key={k} aria-hidden="true" className={`mq-deco pointer-events-none absolute opacity-25 ${s.accent} ${["right-[6%] top-[10%] h-10 w-10", "right-[22%] top-[30%] h-6 w-6", "left-[40%] top-[8%] h-5 w-5", "right-[12%] top-[46%] h-7 w-7"][k]}`} style={{ animationDelay: `${k * 0.8}s` }} />; })}
          <div className="relative flex flex-wrap items-end justify-between gap-6">
            <div>
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${s.dark ? "bg-white/10" : "bg-white shadow-sm"} ${s.accent}`}><s.icon className="h-3.5 w-3.5" />{s.label}</span>
              <h2 className="mt-3 text-[30px] font-semibold leading-tight sm:text-[42px]">{s.gift[0]} <span className={s.accent}>{s.gift[1]}</span></h2>
            </div>
            {codeOf(s, promo) && <div className={`rounded-[20px] p-4 ${s.dark ? "bg-white/10" : "bg-white shadow-sm"}`} data-testid="mq-season-code">
              <div className={`text-[12px] ${s.dark ? "text-white/70" : "text-[var(--muted)]"}`}>Votre code {s.label}{pctOf(promo)}</div>
              <button type="button" onClick={() => copy(codeOf(s, promo))} className={`mt-1 inline-flex items-center gap-2 rounded-xl border-2 border-dashed px-4 py-2 text-[20px] font-bold tracking-[0.12em] ${s.dark ? "border-white/40" : "border-[var(--blue)] text-[var(--blue)]"}`} data-testid="mq-season-code-copy">
                {codeOf(s, promo)}{ok ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </button>
              <div className={`mt-1 text-[11px] ${s.dark ? "text-white/60" : "text-[var(--subtle)]"}`}>{ok ? "Code copié !" : "Appliqué automatiquement au paiement"}</div>
            </div>}
          </div>
          <div className="rail relative mt-6">{items.map((p) => <ProductCard key={p.slug} p={p} rail ribbon={s.ribbon} />)}</div>
          <Link to={`/offres/${seasonKey}`} className={`relative mt-4 inline-flex items-center gap-1 text-[14px] font-semibold ${s.dark ? "text-white" : "text-[var(--link)]"}`} data-testid="mq-season-all">Voir toutes les offres <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
    </section>
  );
}

export function Arguments({ maxPct }) {
  const A = [
    [maxPct ? `−${maxPct} %` : "−40 %", "jusqu'à, par rapport au prix neuf"],
    ["36 mois", "de garantie sur chaque appareil"],
    ["40+", "points de contrôle en atelier"],
    ["≈ 70 kg", "de CO₂ évités par smartphone"],
  ];
  return (
    <section className="py-10 sm:py-14" data-testid="mq-arguments">
      <div className="wrap">
        <SecHead eyebrow="Pourquoi le reconditionné" title="Le même appareil." accent="Bien moins cher." center />
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {A.map(([v, l], k) => (
            <div key={l} className={`card p-5 sm:p-7 ${k === 0 ? "!bg-[var(--blue)] text-white" : ""}`} data-testid={`mq-arg-${k}`}>
              <div className="text-[34px] font-bold leading-none tracking-[-0.03em] sm:text-[46px]">{v}</div>
              <div className={`mt-2 text-[13px] sm:text-[14px] ${k === 0 ? "text-white/80" : "text-[var(--muted)]"}`}>{l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
